import React, { useState, useEffect } from 'react';
import { Play, X, Headphones } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { LazyImage } from '@/components/ui/LazyImage';

function getYouTubeThumbnailAlt(url: string, defaultName: string): string {
  if (url?.includes('roQeEukIkLg')) {
    return 'FIVE LENS Enneagram and Multidimensional Framework overview video testimonial';
  }
  if (url?.includes('t96CzPJ5xt0')) {
    return 'FIVE LENS client transformation and team effectiveness video testimonial';
  }
  if (url?.includes('TOhtlloJQbA')) {
    return 'FIVE LENS Personal Mastery and leadership development video testimonial';
  }
  if (url?.includes('i9qeJTP_Wfw')) {
    return 'FIVE LENS organisational impact and culture shift video testimonial';
  }
  if (url?.includes('Xk034_8P_mI')) {
    return 'FIVE LENS Practitioner certification experience video testimonial';
  }
  return `${defaultName} testimonial`;
}

export function VideoTestimonials() {
  const { t } = useTranslation();
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  
  const [testimonials, setTestimonials] = useState([
    {
      id: 1,
      name: 'Enneagram & Multidimensional Framework',
      role: 'FIVE LENS Overview',
      quote: '"Discover how FIVE LENS integrates the Enneagram with other key dimensions for holistic development."',
      thumbnail: 'https://img.youtube.com/vi/roQeEukIkLg/maxresdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/roQeEukIkLg'
    },
    {
      id: 2,
      name: 'Client Transformation',
      role: 'Team Effectiveness',
      quote: '"See how organisations are reducing friction and aligning their executive teams for optimal performance."',
      thumbnail: 'https://img.youtube.com/vi/t96CzPJ5xt0/maxresdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/t96CzPJ5xt0'
    },
    {
      id: 3,
      name: 'Personal Mastery',
      role: 'Leadership Development',
      quote: '"A deeper look into developing self-awareness and emotional resilience through our unique methodology."',
      thumbnail: 'https://img.youtube.com/vi/TOhtlloJQbA/maxresdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/TOhtlloJQbA'
    },
    {
      id: 4,
      name: 'Organizational Impact',
      role: 'Culture Shift',
      quote: '"Addressing the root cause of behavioral patterns to create sustainable organisational change."',
      thumbnail: 'https://img.youtube.com/vi/i9qeJTP_Wfw/maxresdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/i9qeJTP_Wfw'
    },
    {
      id: 5,
      name: 'Practitioner Experience',
      role: 'Certification',
      quote: '"What it means to become a certified FIVE LENS practitioner and drive real change."',
      thumbnail: 'https://img.youtube.com/vi/Xk034_8P_mI/maxresdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/Xk034_8P_mI'
    }
  ]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await fetch('/api/public/testimonials');
        if (res.ok) {
          const data = await res.json();
          if (data && data.items && Array.isArray(data.items) && data.items.length > 0) {
            const consented = data.items.filter((item: any) => item.consentObtained === true);
            if (consented.length > 0) {
              setTestimonials(consented);
            }
          }
        }
      } catch (error: any) {
        console.warn('Error fetching testimonials:', error);
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <section className="bg-gradient-to-br from-ink to-ink/80 py-24 text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-rule-3 mb-4 block">Client Stories</span>
            <h2 className="text-3xl lg:text-4xl font-serif mb-6">Hear the Impact</h2>
            <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Discover how organisations worldwide are using FIVE LENS to drive measurable transformation in leadership, collaboration, and culture.
            </p>
          </div>
        </ScrollReveal>

        <div className="relative">
          <div className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-8 -mx-6 px-6 lg:-mx-8 lg:px-8 scrollbar-dark">
            {testimonials.map((testimonial, index) => (
              <ScrollReveal 
                key={testimonial.id} 
                delay={0.1 * index}
                className="flex-none w-[85vw] sm:w-[calc(50%-12px)] md:w-[calc(33.333%-16px)] snap-start"
              >
                <button 
                  className="group relative cursor-pointer flex flex-col h-full text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-rule-3 rounded-xl" 
                  onClick={() => setActiveVideo(testimonial.videoUrl)}
                  aria-label={`Play testimonial video from ${testimonial.name}`}
                >
                <div className="relative aspect-video rounded-xl overflow-hidden mb-6 bg-ground/10">
                  {testimonial.thumbnail ? (
                    <LazyImage 
                      src={testimonial.thumbnail} 
                      alt={getYouTubeThumbnailAlt(testimonial.thumbnail || testimonial.videoUrl || '', testimonial.name)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-80 group-hover:opacity-100"
                    />
                  ) : null}
                  <div className="absolute inset-0 bg-ink/40 group-hover:bg-ink/20 transition-colors duration-300" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30 group-hover:bg-rule-3 group-hover:border-rule-3 transition-all duration-300">
                      {testimonial.videoUrl?.includes('iono.fm') ? (
                        <Headphones className="w-6 h-6 text-white" />
                      ) : (
                        <Play className="w-6 h-6 text-white ml-1 fill-white" />
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col flex-grow">
                  <h3 className="text-xl font-serif mb-1">{testimonial.name}</h3>
                  <p className="text-xs font-bold uppercase tracking-wider text-rule-3 mb-4">{testimonial.role}</p>
                  <p className="text-white/70 italic text-sm leading-relaxed mt-auto">{testimonial.quote}</p>
                </div>
              </button>
            </ScrollReveal>
          ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {activeVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
          >
            <div 
              className="absolute inset-0 bg-ink/90 backdrop-blur-sm"
              onClick={() => setActiveVideo(null)}
              aria-hidden="true"
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label="Video Testimonial"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`relative w-full ${activeVideo?.includes('iono.fm') ? 'max-w-2xl h-40' : 'max-w-5xl aspect-video'} bg-black rounded-2xl overflow-hidden shadow-2xl z-10`}
            >
              <button 
                onClick={() => setActiveVideo(null)}
                className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-rule-3 transition-colors"
                aria-label="Close video"
              >
                <X className="w-5 h-5" />
              </button>
              <iframe
                src={`${activeVideo}${activeVideo?.includes('?') ? '&' : '?'}autoplay=1`}
                title="Client Testimonial"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
